import React, { useEffect, useMemo, useRef, useState } from 'react';
import { TextEditor } from 'Core/components/editor';
import { selectCustomerData } from 'Core/redux/customer/selectors';
import { useSelector } from 'react-redux';
import { useTransformOptions, useDocumentUpload } from '../../core/hooks';
import { MOLD_RECEIVING } from '../../core/constants/moldConstants';
import { moldDocuments } from '../../core/prop-types/moldDocuments';
import { FileUploadSingle, SimpleFileUpload, DocumentViewerModal } from '../../core/components';
import { PartService } from 'Service/api/part-service';
import { CustomerService } from 'Service/api/customer-service';
import PropTypes from 'prop-types';
import Select from 'react-select';
import CreatableSelect from 'react-select/creatable';
import WebcamFileUpload from '../../core/components/webcamfileupload/webcam-file-upload';
import { Modal, Button } from 'react-bootstrap';
import { Eye } from 'react-feather';

const customSelectStyles = hasError => ({
  control: base => ({
    ...base,
    minHeight: '38px',
    borderColor: hasError ? '#dc3545' : '#ced4da',
    boxShadow: 'none',
    '&:hover': {
      borderColor: hasError ? '#dc3545' : '#ced4da',
    },
  }),
  valueContainer: base => ({
    ...base,
    padding: '2px 12px',
    minHeight: '38px',
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
  }),
  clearIndicator: base => ({
    ...base,
    display: 'none',
  }),
  input: base => ({
    ...base,
    margin: 0,
    padding: 0,
  }),
  singleValue: base => ({
    ...base,
    margin: 0,
    color: '#212529', // Match bootstrap's form-control text color
  }),
  placeholder: base => ({
    ...base,
    margin: 0,
    color: '#6c757d',
  }),
  multiValue: base => ({
    ...base,
    backgroundColor: '#e1efff',
  }),
  multiValueLabel: base => ({
    ...base,
    color: '#0b2c3d',
    fontWeight: 500,
  }),
});

const normalizePartsForSelect = parts => {
  if (!Array.isArray(parts)) {
    return [];
  }

  return parts
    .filter(part => !!part)
    .map(part => {
      const partId =
        part.partId ??
        part.PartId ??
        part.partID ??
        part.id ??
        part.value ??
        null;
      const partNumber =
        part.partNumber ??
        part.PartNumber ??
        part.label ??
        part.partname ??
        part.PartName ??
        '';

      return {
        value: partId,
        label: partNumber,
        partId,
        partNumber,
      };
    });
};

const initData = {
  moldInfoID: 0,
  CustomerId: 0,
  toolID: '',
  customerName: '',
  customerContact: '',
  customerEmail: '',
  customerPhone: '',
  moldNumber: '',
  moldDescription: '',
  partID: 0,
  description: '',
  materialType: '',
  cavity: '',
  shipmentDate: '',
  receivedDate: '',
  carrier: '',
  trackingNumber: '',
  conditionofShipment: '',
  inspectedBy: '',
  additionalPicture: [],
  noVisibleDamage: false,
  noRustOrCorrosion: false,
  properLabeling: false,
  moldWidth: 0,
  moldLength: 0,
  moldHeight: 0,
  functionalTest: false,
  functionalTestPicture: '',
  additionalComments: '',
  unloadingApproval: '',
  qualityControlApprovedBy: '',
  engineeringApprovedBy: '',
  submittedBy: '',
  moldDocuments: [],
  parts: [],
};

const buildInitialFormState = source => {
  const safeSource = source || {};
  const normalizedParts =
    normalizePartsForSelect(
      safeSource.parts || safeSource.moldParts || safeSource.selectedParts,
    ) || [];
  const firstExistingPartId =
    safeSource.partID ||
    normalizedParts.find(option => option.value)?.value ||
    0;

  // Exclude customer email, phone, and contact from initial state
  // These will be populated from getCustomerById API call
  const { customerEmail, customerPhone, customerContact, ...restSource } = safeSource;

  return {
    ...initData,
    ...restSource,
    parts: normalizedParts,
    partID: firstExistingPartId || 0,
    // Keep email, phone, contact as empty strings - they'll be populated from getCustomerById
    customerEmail: '',
    customerPhone: '',
    customerContact: '',
  };
};

const MoldReceivingAddEditForm = ({
  data,
  onChanges,
  onFileUpload,
  id,
  formRef,
  invalidFields = [],
  showValidation = false,
  isEditMode = false,
  onLoadingChange,
}) => {
  const formReference = useRef(null);
  const [processedImageSegments, setProcessedImageSegments] = useState({});
  const [partsList, setPartsList] = useState([]);
  const [isFieldsLocked, setIsFieldsLocked] = useState(false);
  const [showPartsModal, setShowPartsModal] = useState(false);
  const [showPartsSelector, setShowPartsSelector] = useState(false);
  const [showDocumentViewer, setShowDocumentViewer] = useState(false);
  const [documentViewerUrl, setDocumentViewerUrl] = useState('');
  const [documentViewerName, setDocumentViewerName] = useState('');
  const { uploadDocuments, loading } = useDocumentUpload();
  const partOptions = partsList.map(part => ({
    value: part.id,
    label: part.partNumber,
    partId: part.id,
    partNumber: part.partNumber,
  }));
  const isViewMode = isEditMode || isFieldsLocked;

  // Pass loading state to parent component
  useEffect(() => {
    if (onLoadingChange) {
      onLoadingChange(loading);
    }
  }, [loading, onLoadingChange]);

  // Function to process image segments from API response and map them to file upload components
  const processImageSegments = (imageSegments) => {
    if (!imageSegments || !Array.isArray(imageSegments)) {
      return {};
    }
    
    const mappedFiles = {};
    let additionalPicturesLabel = 'Additional Pictures'; // Default label
    
    imageSegments.forEach(segment => {
      const { record, imageUrl, segment: segmentName, id } = segment;
      
      // Map record names to field names for Mold Receiving
      const recordToFieldMap = {
        'Upload Picture of damage': 'UPLOAD_PICTURE_OF_DAMAGE',
        'Mold Picture on the Trailer': 'MOLD_PICTURE_ON_THE_TRAILER',
        'Mold Picture Unloaded': 'MOLD_PICTURE_UNLOADED',
        'Mold Picture open': 'MOLD_PICTURE_OPEN',
        'Additional Picture 1': 'ADDITIONAL_PICTURES_ONE',
        'Additional Picture 2': 'ADDITIONAL_PICTURES_TWO',
        'Additional Picture 3': 'ADDITIONAL_PICTURES_THREE',
        'Additional Picture 4': 'ADDITIONAL_PICTURES_FOUR',
        'Additional Picture 5': 'ADDITIONAL_PICTURES_FIVE',
        'Additional Picture 6': 'ADDITIONAL_PICTURES_SIX',
        'Additional Pictures': 'ADDITIONAL_PICTURES_ONE' // Default fallback
      };
      
      const fieldName = recordToFieldMap[record];
      
      if (fieldName) {
        if (!mappedFiles[fieldName]) {
          mappedFiles[fieldName] = [];
        }
        
        const fileObject = {
          id: id,
          name: `${segmentName}_${id}.jpg`,
          path: imageUrl,
          fileName: `${segmentName}_${id}.jpg`,
          filePath: imageUrl
        };
        mappedFiles[fieldName].push(fileObject);
      } else {
        // If no predefined mapping found, map to Additional Picture 1
        if (!mappedFiles['ADDITIONAL_PICTURES_ONE']) {
          mappedFiles['ADDITIONAL_PICTURES_ONE'] = [];
        }
        
        const fileObject = {
          id: id,
          name: `${segmentName}_${id}.jpg`,
          path: imageUrl,
          fileName: `${segmentName}_${id}.jpg`,
          filePath: imageUrl
        };
        mappedFiles['ADDITIONAL_PICTURES_ONE'].push(fileObject);
        additionalPicturesLabel = record; // Update label to the new record name
      }
    });
    
    // Store the additional pictures label for later use
    if (additionalPicturesLabel !== 'Additional Pictures') {
      mappedFiles.additionalPicturesLabel = additionalPicturesLabel;
    }
    
    return mappedFiles;
  };

  const [formData, setFormData] = useState(buildInitialFormState(data));

const isInitialMount = useRef(true);
const prevFormDataRef = useRef();
const skipNextDataSyncRef = useRef(false);
const lastFetchedCustomerIdRef = useRef(null);
const [isCustomerDetailsLoading, setIsCustomerDetailsLoading] = useState(false);

  const fetchAndMapCustomerDetails = async customerId => {
    const numericCustomerId = Number(customerId);
    if (!numericCustomerId || numericCustomerId <= 0) {
      return;
    }
    if (lastFetchedCustomerIdRef.current === numericCustomerId) {
      return;
    }

    lastFetchedCustomerIdRef.current = numericCustomerId;
    setIsCustomerDetailsLoading(true);

    try {
      const response = await CustomerService.getCustomerById(numericCustomerId);
      if (response?.response) {
        const customerData = response.response;
        setFormData(prevData => ({
          ...prevData,
          CustomerId: numericCustomerId,
          customerEmail: customerData.email ?? customerData.customerEmail ?? '',
          customerPhone: customerData.phone ?? customerData.customerPhone ?? '',
          customerContact: customerData.customerContact ?? customerData.contact ?? '',
        }));
      } else {
        setFormData(prevData => ({
          ...prevData,
          CustomerId: numericCustomerId,
        }));
      }
    } catch (error) {
      console.error('Error fetching customer details:', error);
      setFormData(prevData => ({
        ...prevData,
        CustomerId: numericCustomerId,
      }));
    } finally {
      setIsCustomerDetailsLoading(false);
    }
  };

  useEffect(() => {
    // Pass the form reference to parent if formRef prop exists
    if (formRef) {
      formRef(formReference);
    }
  }, [formRef]);

  useEffect(() => {
    const fetchParts = async () => {
      try {
        const response = await PartService.getAllPartsList();
        if (response?.response) {
          setPartsList(response.response);
        }
      } catch (error) {
        console.error('Error fetching parts list:', error);
      }
    };

    fetchParts();
  }, []);

  // Process image segments when data changes
  useEffect(() => {
    if (data?.moldReceivingData?.imageSegments) {
      const mappedFiles = processImageSegments(data.moldReceivingData.imageSegments);
      setProcessedImageSegments(mappedFiles);
    } else if (data?.imageSegments) {
      const mappedFiles = processImageSegments(data.imageSegments);
      setProcessedImageSegments(mappedFiles);
    }
  }, [data]);

  //const [formData, setFormData] = useState(data ? data : initData);
 // const prevFormDataRef = useRef();

// Replace the existing useEffect that calls onChanges
useEffect(() => {
  // This prevents the effect from running on the initial component mount
  if (isInitialMount.current) {
    isInitialMount.current = false;
    prevFormDataRef.current = formData;
  } else {
    // Only call onChanges if formData actually changed (deep comparison of key fields)
    const hasChanged = JSON.stringify(prevFormDataRef.current) !== JSON.stringify(formData);
    
    if (hasChanged && onChanges) {
      prevFormDataRef.current = formData;
      skipNextDataSyncRef.current = true;
      onChanges(formData);
    }
  }
}, [formData]);
useEffect(() => {
  if (skipNextDataSyncRef.current) {
    skipNextDataSyncRef.current = false;
    return;
  }

  if (data && Object.keys(data).length > 0) {
    const newFormData = buildInitialFormState(data);
    setFormData(newFormData);
    // Reset field lock when new data comes in
    setIsFieldsLocked(false);
    
    // If CustomerId is set from getMoldDetailsByToolID response, fetch customer details once
    if (newFormData.CustomerId && newFormData.CustomerId > 0) {
      fetchAndMapCustomerDetails(newFormData.CustomerId);
    }
  }
}, [data]);
  // Add this near your other useState/useRef declarations
// const isInitialMount = useRef(true);

// // Add this new useEffect alongside your others
// useEffect(() => {
//   // This prevents the effect from running on the initial component mount
//   if (isInitialMount.current) {
//     isInitialMount.current = false;
//   } else {
//     // On any subsequent change to formData, notify the parent component
//     if (onChanges) {
//       onChanges(formData);
//     }
//   }
// }, [formData, onChanges]);

  // Update formData when data prop changes (e.g., when API response comes back)
  // useEffect(() => {
  //   if (data) {
  //     setFormData(data);
  //   }
  // }, [data]);

  // // Debug log when moldInfoID changes
  // useEffect(() => {
  //   console.log('MoldReceivingAddEditForm: formData.moldInfoID changed to:', formData.moldInfoID);
  //   console.log('MoldReceivingAddEditForm: formData object after moldInfoID change:', formData);
  //   console.log('MoldReceivingAddEditForm: useEffect for moldInfoID change triggered');
  // }, [formData.moldInfoID]);

  // // Debug log when formData changes
  // useEffect(() => {
  //   console.log('MoldReceivingAddEditForm: formData changed, moldInfoID is now:', formData.moldInfoID);
  //   console.log('MoldReceivingAddEditForm: formData.moldDocuments is now:', formData.moldDocuments);
  //   console.log('MoldReceivingAddEditForm: formData.moldDocuments length is now:', formData.moldDocuments?.length);
  //   console.log('MoldReceivingAddEditForm: useEffect for formData change triggered');
  //   console.log('MoldReceivingAddEditForm: All WebcamFileUpload components should now receive moldInfoID:', formData.moldInfoID);
    
  //   // Debug: Log what each WebcamFileUpload will receive
  //   console.log('MoldReceivingAddEditForm: WebcamFileUpload components will receive maintenanceData:', {
  //     moldInfoID: formData.moldInfoID,
  //     moldDocuments: formData.moldDocuments,
  //     moldDocumentsLength: formData.moldDocuments?.length,
  //     toolID: formData.toolID
  //   });
  // }, [formData]);
  const customers = useSelector(selectCustomerData);
  const customerOptions = useTransformOptions(
    customers,
    'id',
    'customerName',
    '',
    'Select Customer',
  );

  //const [moldDocuments, setMoldDocuments] = useState(data?.moldDocuments || []);

  // const updateFormField = (fieldName, value) => {
  //   if (isFieldsLocked) {
  //     console.log('MoldReceivingAddEditForm: Fields are locked. Ignoring change to:', fieldName);
  //     return;
  //   }
  //   setFormData(prevData => {
  //     const updatedData = {
  //       ...prevData,
  //       [fieldName]: value,
  //     };
  //     onChanges(updatedData);
  //     return updatedData;
  //   });
  // };

  const updateFormField = (fieldName, value) => {
    if (isFieldsLocked) {
      return;
    }
    
    setFormData(prevData => {
      let processedValue;
      
      // Handle callback functions (for moldDocuments updates)
      if (typeof value === 'function') {
        processedValue = value(prevData[fieldName]);
      } else {
        // Convert mold dimensions to integers (multiply by 100 to preserve 2 decimal places)
        if (['moldWidth', 'moldHeight', 'moldLength'].includes(fieldName)) {
          // Convert to integer by multiplying by 100 to preserve 2 decimal places
          // e.g., 0.02 becomes 2, 12.5 becomes 1250
          processedValue = value === '' || value === null || value === undefined ? 0 : Math.round(parseFloat(value) * 100) || 0;
        } else {
          processedValue = value;
        }
      }
      
      const updatedData = {
        ...prevData,
        [fieldName]: processedValue,
      };
      
      // Don't call onChanges here - the useEffect will handle it to prevent infinite loops
      
      return updatedData;
    });
  };

  const handlePartsChange = selectedOptions => {
    if (isFieldsLocked) {
      return;
    }

    const normalizedSelections = (selectedOptions || []).map(option => ({
      value: option.value ?? null,
      label: option.label,
      partId: option.value ?? null,
      partNumber: option.label,
    }));

    setFormData(prev => {
      const firstWithId = normalizedSelections.find(option => option.value);
      return {
        ...prev,
        parts: normalizedSelections,
        partID: firstWithId ? firstWithId.value : 0,
      };
    });
  };

  const handleCreatePart = inputValue => {
    if (!inputValue) {
      return;
    }
    const newOption = {
      value: null,
      label: inputValue,
      partId: null,
      partNumber: inputValue,
    };

    handlePartsChange([...(formData.parts || []), newOption]);
  };

  const handleCustomerChange = async selectedOption => {
    const customerId = selectedOption ? selectedOption.value : null;

    if (customerId && (customerId > 0 || parseInt(customerId) > 0)) {
      const numericCustomerId = parseInt(customerId);

      if (lastFetchedCustomerIdRef.current === numericCustomerId) {
        return;
      }

      setFormData(prevData => ({
        ...prevData,
        CustomerId: numericCustomerId,
        customerEmail: '',
        customerPhone: '',
        customerContact: '',
      }));

      await fetchAndMapCustomerDetails(numericCustomerId);
    } else {
      lastFetchedCustomerIdRef.current = null;
      setIsCustomerDetailsLoading(false);
      setFormData(prevData => ({
        ...prevData,
        CustomerId: '',
        customerEmail: '',
        customerPhone: '',
        customerContact: '',
      }));
    }
  };

  const renderCustomerDetailField = ({ id, label, fieldName, type = 'text' }) => (
    <div className="input-blocks">
      <label className="form-label" htmlFor={id}>
        {label}
      </label>
      <div className="position-relative">
        <input
          type={type}
          value={isCustomerDetailsLoading ? '' : formData[fieldName]}
          onChange={e => updateFormField(fieldName, e.target.value)}
          className={`form-control${showValidation && invalidFields.includes(fieldName) ? ' is-invalid' : ''}`}
          placeholder={isCustomerDetailsLoading ? 'Loading...' : ''}
          id={id}
          disabled={true}
        />
        {isCustomerDetailsLoading && (
          <span
            className="spinner-border spinner-border-sm text-secondary position-absolute top-50 end-0 translate-middle-y me-2"
            role="status"
            aria-hidden="true"
          />
        )}
      </div>
      <div className="invalid-feedback">Please provide a valid {label}</div>
    </div>
  );
  const selectedParts = formData.parts || [];
  const sortedPartOptions = useMemo(() => {
    if (!selectedParts.length) {
      return partOptions;
    }
    const selectedIds = new Set(
      selectedParts.map(option => option.value ?? option.partId ?? option.partNumber),
    );
    return [...partOptions].sort((a, b) => {
      const aSelected = selectedIds.has(a.value);
      const bSelected = selectedIds.has(b.value);
      if (aSelected === bSelected) {
        return (a.label || '').localeCompare(b.label || '');
      }
      return aSelected ? -1 : 1;
    });
  }, [partOptions, selectedParts]);
  const partsSummaryLabel = selectedParts.length
    ? `${selectedParts.length} part${selectedParts.length > 1 ? 's' : ''} selected`
    : 'No parts selected';
  const shouldShowSelect = !isViewMode || showPartsSelector;
  const shouldShowSummary = isViewMode && !showPartsSelector;

  useEffect(() => {
    if (!isViewMode && showPartsSelector) {
      setShowPartsSelector(false);
    }
  }, [isViewMode, showPartsSelector]);
  const handleOpenPartsModal = () => {
    if (!selectedParts.length) return;
    setShowPartsModal(true);
  };
  const handleClosePartsModal = () => setShowPartsModal(false);

  // Callback to handle when moldInfoID is saved from the webcam modal
  const handleMoldInfoIDSaved = (newMoldInfoID, newToolID) => {
    if (newMoldInfoID && newMoldInfoID > 0) {
      // Create the updated data
      const updatedData = {
        ...formData,
        moldInfoID: newMoldInfoID,
        toolID: newToolID || formData.toolID, // Use new toolID if provided, otherwise keep existing
      };
      
      // Update the state - the useEffect will notify parent
      setFormData(updatedData);

      // Lock fields for edit pages after first save from capture flow
      if (isEditMode && !isFieldsLocked) {
        setIsFieldsLocked(true);
      }
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (file) {
      // Create a temporary document entry
      const document = {
        documentId: Math.floor(Math.random() * 1000) + 100, // Generate a unique ID
        documentName: e.target.id, // Use the input ID as document name
        documentPath: null, // Will be set after successful upload
        fileName: file.name,
        file: file,
        isPendingUpload: true // Flag to indicate this needs to be uploaded
      };
      
      console.log('Creating temporary document:', document);
      
      // Update the moldDocuments array immediately
      const updatedMoldDocuments = [...(formData.moldDocuments || []), document];
      console.log('Adding temporary document to moldDocuments:', updatedMoldDocuments);
      updateFormField('moldDocuments', updatedMoldDocuments);
      
      // Also call the parent's onFileUpload callback if provided
      if (onFileUpload) {
        onFileUpload(e.target.id, file);
      }
      
      // Immediately upload the file
      try {
        console.log(`Uploading file immediately: ${file.name}`);
        
        const docFormData = new FormData();
        docFormData.append('file', file);
        docFormData.append('fileCategoryId', e.target.id);
        docFormData.append('uploadCategory', `MOLD${formData.toolID || 'TEMP'}`);
        
        // Use the upload hook directly
        const result = await uploadDocuments(docFormData);
        
        if (result && result.relativePath) {
          console.log(`File uploaded successfully: ${result.relativePath}`);
          
          // Update the document with the actual file path using a callback to get the latest state
          updateFormField('moldDocuments', (currentMoldDocuments) => {
            const finalMoldDocuments = (currentMoldDocuments || []).map(doc => 
              doc.documentName === e.target.id && doc.isPendingUpload
                ? {
                    ...doc,
                    documentPath: result.relativePath,
                    isPendingUpload: false
                  }
                : doc
            );
            
            console.log('Final mold documents after upload:', finalMoldDocuments);
            return finalMoldDocuments;
          });
          
          console.log(`Document path updated: ${result.relativePath}`);
        } else {
          console.error('Upload failed: No relativePath in response');
          // Remove the failed document using callback
          updateFormField('moldDocuments', (currentMoldDocuments) => {
            return (currentMoldDocuments || []).filter(doc => 
              !(doc.documentName === e.target.id && doc.isPendingUpload)
            );
          });
        }
      } catch (error) {
        console.error('Error uploading file:', error);
        // Remove the failed document using callback
        updateFormField('moldDocuments', (currentMoldDocuments) => {
          return (currentMoldDocuments || []).filter(doc => 
            !(doc.documentName === e.target.id && doc.isPendingUpload)
          );
        });
      }
    }
  };

  const getDocumentPath = inputId => {
    const matchingDoc = formData.moldDocuments.find(doc => doc.documentName == inputId);

    let fileName = '';
    if (matchingDoc?.documentPath && matchingDoc.documentPath !== null) {
      // Only process if documentPath is not null (i.e., upload completed)
      const encodedFileName = matchingDoc.documentPath?.split('/').pop();
      const decoded = decodeURIComponent(encodedFileName);
      const lastUnderscore = decoded.lastIndexOf('_');
      const extensionIndex = decoded.lastIndexOf('.');

      if (lastUnderscore !== -1 && extensionIndex !== -1 && lastUnderscore < extensionIndex) {
        fileName = decoded?.slice(0, lastUnderscore) + decoded?.slice(extensionIndex);
      } else {
        fileName = decoded;
      }
    }

    return matchingDoc && matchingDoc.documentPath ? { fileUrl: matchingDoc.documentPath, fileName: fileName } : '';
  };

  const convertDocumentsForWebCamFileUpload = inputId => {
    // First check if we have processed image segments for this field
    if (processedImageSegments[inputId] && processedImageSegments[inputId].length > 0) {
      return processedImageSegments[inputId];
    }
    
    // Fallback to original logic
    const matchedDocs = formData.moldDocuments
      .filter(doc => doc.documentName == inputId && doc.documentPath !== null)
      .map(doc => {
        let fileName = '';

        if (doc.documentPath !== undefined && doc.documentPath !== null) {
          const encodedFileName = doc.documentPath.split('/').pop();
          const decoded = decodeURIComponent(encodedFileName);
          const lastUnderscore = decoded.lastIndexOf('_');
          const extensionIndex = decoded.lastIndexOf('.');

          if (lastUnderscore !== -1 && extensionIndex !== -1 && lastUnderscore < extensionIndex) {
            fileName = decoded.slice(0, lastUnderscore) + decoded.slice(extensionIndex);
          } else {
            fileName = decoded;
          }
        }

        return {
          id: doc.documentId,
          name: fileName,
          path: doc.documentPath,
        };
      });

    return matchedDocs;
  };

  // Function to check if there are images for a specific field
  const hasImagesForField = (fieldName) => {
    const documents = convertDocumentsForWebCamFileUpload(fieldName);
    const hasImages = documents && documents.length > 0;
    return hasImages;
  };

  const updateMoldDocuments = newDocuments => {
    const documents = newDocuments.map(document => ({
      documentId: Math.floor(Math.random() * 101),
      documentName: document.id,
      documentPath: document.path, // This should be the relativePath from upload API response
    }));
    
    const moldDocuments = [...(formData.moldDocuments || []), ...documents];
    
    updateFormField('moldDocuments', moldDocuments);
  };

  const [isDamaged, setIsDamaged] = useState(formData.conditionofShipment == 'Damaged');

  // Sync isDamaged state with formData changes
  useEffect(() => {
    const newIsDamaged = formData.conditionofShipment === 'Damaged';
    if (newIsDamaged !== isDamaged) {
      setIsDamaged(newIsDamaged);
    }
  }, [formData.conditionofShipment, isDamaged]);

  return (
    <>
    <form ref={formReference}>
      {/* <form className="needs-validation" ref={formReference} noValidate> */}
      {/* Customer Information*/}
      <h5 className="header-five">Customer Information</h5>
      <div className="form-row row m-2">
        <div className="col-lg-3 col-md-3 col-sm-12">
          <div className="input-blocks ">
            {/* <label className="form-label" htmlFor="toolId"> */}
            <label className="form-label">
              ToolID <span className="text-danger">*</span>
            </label>
            <input
              placeholder=""
              value={formData.toolID}
              disabled={true}
              onChange={e => updateFormField('toolID', e.target.value)}
              type="text"
              id="toolId"
              className="form-control"
              // className={`form-control${showValidation && invalidFields.includes('toolID') ? ' is-invalid' : ''}`}
              // required
            />
            <div className="invalid-feedback">Please provide a valid ToolID</div>
          </div>
        </div>

        <div className="col-lg-3 col-md-3 col-sm-12">
          <div className="input-blocks ">
            <label className="form-label" htmlFor="toolId">
              Previous ToolID
            </label>
            <input
              placeholder=""
              value={formData.prevToolID}
              onChange={e => updateFormField('prevToolID', e.target.value)}
              type="text"
              id="prevToolID"
              className="form-control"
            />
          </div>
        </div>

        <div className="col-lg-3 col-md-3 col-sm-12">
          <div className="input-blocks">
            <label>
              Customer Name <span className="text-danger">*</span>
            </label>
            {/* <select
              value={formData.CustomerId}
              onChange={e => updateFormField('CustomerId', e.target.value)}
              className={`form-control${showValidation && invalidFields.includes('CustomerId') ? ' is-invalid' : ''}`}
              required
            >
              {customerOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select> 

            <div className="invalid-feedback">Please provide a valid Customer Name</div> */}

            <Select
              options={customerOptions}
              placeholder="Select Customer"
              isClearable
              isSearchable
              backspaceRemovesValue={true}
              value={customerOptions.find(option => option.value == formData.CustomerId)}
              onChange={handleCustomerChange}
              styles={customSelectStyles(showValidation && invalidFields.includes('CustomerId'))}
            />
            {showValidation && invalidFields.includes('CustomerId') && (
              <div className="invalid-feedback" style={{ display: 'block' }}>
                Please provide a valid Customer Name
              </div>
            )}
          </div>
        </div>
        <div className="col-lg-3 col-md-3 col-sm-12">
          {renderCustomerDetailField({
            id: 'customerEmail',
            label: 'Customer Email',
            fieldName: 'customerEmail',
            type: 'email',
          })}
        </div>
      </div>
      <div className="row m-2">
        <div className="col-lg-3 col-md-3 col-sm-12">
          {renderCustomerDetailField({
            id: 'customerPhone',
            label: 'Customer Phone',
            fieldName: 'customerPhone',
          })}
        </div>

        <div className="col-lg-3 col-md-3 col-sm-12">
          {renderCustomerDetailField({
            id: 'customerContact',
            label: 'Customer Contact',
            fieldName: 'customerContact',
          })}
        </div>
      </div>

      {/*Mold Information*/}
      <h5 className="header-five">Mold Information</h5>
      <div className="row m-2">
        <div className="col-lg-3 col-md-3 col-sm-12">
          <div className="input-blocks">
            {/* <label className="form-label" htmlFor="moldNumber"> */}
            <label className="form-label">
              Mold Number <span className="text-danger">*</span>
            </label>
            <input
              type="text"
              value={formData.moldNumber}
              onChange={e => updateFormField('moldNumber', e.target.value)}
              // className="form-control"
              // className={`form-control${showValidation && invalidFields.includes('moldNumber') ? ' is-invalid' : ''}`}
              placeholder=""
              id="moldNumber"
              disabled={true}
              // required
            />
            <div className="invalid-feedback">Please provide a valid Mold Number</div>
          </div>
        </div>
        <div className="col-lg-3 col-md-3 col-sm-12">
          <div className="input-blocks">
            <label className="form-label" htmlFor="moldDescription">
              Mold Description
            </label>
            <input
              type="text"
              value={formData.moldDescription}
              onChange={e => updateFormField('moldDescription', e.target.value)}
              // className="form-control"
              className={`form-control${showValidation && invalidFields.includes('moldDescription') ? ' is-invalid' : ''}`}
              placeholder=""
              id="moldDescription"
            />
            <div className="invalid-feedback">Please provide a valid Mold Description</div>
          </div>
        </div>
        <div className="col-lg-3 col-md-3 col-sm-12">
          <div className="input-blocks">
            <label>
              Part
            </label>
            <div className="d-flex align-items-start gap-2">
              <div className="flex-grow-1">
                {shouldShowSummary ? (
                  <input
                    type="text"
                    className="form-control"
                    value={partsSummaryLabel}
                    readOnly
                    onClick={() => {
                      if (!selectedParts.length && !partOptions.length) return;
                      setShowPartsSelector(true);
                    }}
                    style={{ cursor: selectedParts.length || partOptions.length ? 'pointer' : 'not-allowed' }}
                  />
                ) : (
                  <CreatableSelect
                    isMulti
                    closeMenuOnSelect={false}
                    options={sortedPartOptions}
                    placeholder="Select or type parts"
                    value={selectedParts}
                    onChange={handlePartsChange}
                    onCreateOption={handleCreatePart}
                    styles={customSelectStyles(showValidation && invalidFields.includes('parts'))}
                    onMenuClose={() => {
                      if (isViewMode) {
                        setShowPartsSelector(false);
                      }
                    }}
                  />
                )}
              </div>
              <Button
                variant="outline-secondary"
                className="d-flex align-items-center justify-content-center mt-1"
                title="View all parts"
                type="button"
                onClick={handleOpenPartsModal}
                disabled={!selectedParts.length}
              >
                <Eye size={16} />
              </Button>
            </div>
          </div>
        </div>

        {/* <div className="col-lg-3 col-md-3 col-sm-12">
          <div className="input-blocks">
            <label className="form-label" htmlFor="partName">
              Part ID <span className="text-danger">*</span>
            </label>
            <input
              type="text"
              value={formData.partName}
              onChange={e => updateFormField('partName', e.target.value)}
              className="form-control"
              placeholder=""
              id="partName"
              required
            />
            <div className="invalid-feedback">Please provide a valid Part ID</div>
          </div>
        </div> */}
        {/* <div className="col-lg-3 col-md-3 col-sm-12">
          <div className="input-blocks">
            <label className="form-label" htmlFor="partNumber">
              Part Number <span className="text-danger">*</span>
            </label>
            <input
              type="text"
              value={formData.partNumber}
              onChange={e => updateFormField('partNumber', e.target.value)}
              className="form-control"
              placeholder=""
              id="partNumber"
              required
            />
            <div className="invalid-feedback">Please provide a valid Part Number</div>
          </div>
        </div> */}
        <div className="col-lg-3 col-md-3 col-sm-12">
          <div className="input-blocks">
            <label className="form-label" htmlFor="materialType">
              Material Type
            </label>
            <input
              type="text"
              value={formData.materialType}
              onChange={e => updateFormField('materialType', e.target.value)}
              // className="form-control"
              className={`form-control${showValidation && invalidFields.includes('materialType') ? ' is-invalid' : ''}`}
              placeholder=""
              id="materialType"
            />
            <div className="invalid-feedback">Please provide a valid Material Type</div>
          </div>
        </div>
      </div>

      <Modal show={showPartsModal} onHide={handleClosePartsModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>Selected Parts</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedParts.length === 0 ? (
            <p className="mb-0 text-muted">No parts selected.</p>
          ) : (
            <ul className="list-group list-group-flush">
              {selectedParts.map((part, index) => (
                <li
                  key={`${part.value ?? part.partId ?? part.partNumber ?? index}`}
                  className="list-group-item"
                >
                  <span className="fw-medium">{part.partNumber || part.label || 'N/A'}</span>
                </li>
              ))}
            </ul>
          )}
        </Modal.Body>
      </Modal>

      <div className="row m-2">
        <div className="col-lg-3 col-md-3 col-sm-12">
          <div className="input-blocks">
            <label className="form-label" htmlFor="cavity">
              Cavity
            </label>
            <input
              type="text"
              value={formData.cavity}
              onChange={e => updateFormField('cavity', e.target.value)}
              // className="form-control"
              className={`form-control${showValidation && invalidFields.includes('cavity') ? ' is-invalid' : ''}`}
              placeholder=""
              id="cavity"
            />
            <div className="invalid-feedback">Please provide a valid Cavity Number</div>
          </div>
        </div>
      </div>

      {/* Shipment Information*/}
      <h5 className="header-five">Shipment Information</h5>
      <div className="row m-2">
        <div className="col-lg-3 col-md-3 col-sm-12">
          <div className="input-blocks">
            <label>
              Shipment Date <span className="text-danger">*</span>
            </label>
            <input
              type="date"
              value={formData.shipmentDate?.split('T')[0] || ''}
              onChange={e => updateFormField('shipmentDate', e.target.value)}
              // className="form-control"
              className={`form-control${showValidation && invalidFields.includes('shipmentDate') ? ' is-invalid' : ''}`}
            />
          </div>
        </div>
        <div className="col-lg-3 col-md-3 col-sm-12">
          <div className="input-blocks">
            <label>
              Received Date <span className="text-danger">*</span>
            </label>
            <input
              type="date"
              value={formData.receivedDate?.split('T')[0] || ''}
              onChange={e => updateFormField('receivedDate', e.target.value)}
              // className="form-control"
              className={`form-control${showValidation && invalidFields.includes('receivedDate') ? ' is-invalid' : ''}`}
            />
          </div>
        </div>
        <div className="col-lg-3 col-md-3 col-sm-12">
          <div className="input-blocks">
            <label>Carrier</label>
            <input
              type="text"
              value={formData.carrier}
              onChange={e => updateFormField('carrier', e.target.value)}
              className="form-control"
            />
          </div>
        </div>
        <div className="col-lg-3 col-md-3 col-sm-12">
          <div className="input-blocks">
            <label>Tracking Number</label>
            <input
              type="text"
              value={formData.trackingNumber}
              onChange={e => updateFormField('trackingNumber', e.target.value)}
              className="form-control"
            />
          </div>
        </div>
      </div>

      <div className="row m-2">
        <div className="col-lg-3 col-md-3 col-sm-12">
          <div className="input-blocks">
            <label>Condition of Shipment</label>
            <div className="btn-group" role="group" aria-label="Basic radio toggle button group">
              <input
                type="radio"
                className="btn-check"
                name="btnradioCond"
                id="btnradioGood"
                defaultChecked=""
                checked={formData.conditionofShipment == 'Good'}
                value="Good"
                onChange={e => {
                  updateFormField('conditionofShipment', e.target.value);
                  setIsDamaged(false);
                }}
              />
              <label className="btn btn-outline-primary" htmlFor="btnradioGood">
                Good
              </label>
              <input
                type="radio"
                className="btn-check"
                name="btnradioCond"
                id="btnradioDamaged"
                checked={formData.conditionofShipment == 'Damaged'}
                value="Damaged"
                onChange={e => {
                  updateFormField('conditionofShipment', e.target.value);
                  setIsDamaged(true);
                }}
              />
              <label className="btn btn-outline-primary" htmlFor="btnradioDamaged">
                Damaged
              </label>
            </div>
          </div>
        </div>

        <div className="col-lg-3 col-md-3 col-sm-12">
          {isDamaged && (
            <div className="input-blocks text-truncate">
              <label>Upload Picture of damage</label>
              <WebcamFileUpload
                id={MOLD_RECEIVING.UPLOAD_PICTURE_OF_DAMAGE}
                onChange={updateMoldDocuments}
                documents={convertDocumentsForWebCamFileUpload(
                  MOLD_RECEIVING.UPLOAD_PICTURE_OF_DAMAGE,
                )}
                context="receiving"
                maintenanceData={formData}
                buttonLabel="Upload Picture of damage"
                showManageImages={hasImagesForField(MOLD_RECEIVING.UPLOAD_PICTURE_OF_DAMAGE)}
                moldInfoID={formData.moldInfoID}
                onMaintenanceSaved={handleMoldInfoIDSaved}
              />
            </div>
          )}
        </div>

        <div className="col-lg-6 col-md-6 col-sm-12">
          <div className="input-blocks">
            <label>Inspected By</label>
            <input
              type="text"
              value={formData.inspectedBy}
              onChange={e => updateFormField('inspectedBy', e.target.value)}
              className="form-control"
            />
          </div>
        </div>
      </div>

      {/* Documentation*/}
      <h5 className="header-five">Documentation</h5>
      <div className="row m-2">
        <div className="col-lg-6 col-md-6 col-sm-12">
          <div className="input-blocks">
            <label>Mold Drawing(PDF/DWG)</label>
            {/* <div className="d-flex"> */}
            <SimpleFileUpload
              id={MOLD_RECEIVING.MOLD_DRAWING_ONE}
              onChange={handleFileUpload}
              value={getDocumentPath(MOLD_RECEIVING.MOLD_DRAWING_ONE)?.fileName}
              filePath={getDocumentPath(MOLD_RECEIVING.MOLD_DRAWING_ONE)?.fileUrl}
              onViewClick={(filePath, fileName) => {
                setDocumentViewerUrl(filePath);
                setDocumentViewerName(fileName);
                setShowDocumentViewer(true);
              }}
            />
            {/* <button class="btn btn-sm btn-success">
                <i class="fa fa-file-image"></i>
              </button> */}
            {/* </div> */}
          </div>
        </div>
        <div className="col-lg-6 col-md-6 col-sm-12">
          <div className="input-blocks">
            <label>Mold Drawing(PDF/DWG)</label>
            <SimpleFileUpload
              id={MOLD_RECEIVING.MOLD_DRAWING_TWO}
              onChange={handleFileUpload}
              value={getDocumentPath(MOLD_RECEIVING.MOLD_DRAWING_TWO)?.fileName}
              filePath={getDocumentPath(MOLD_RECEIVING.MOLD_DRAWING_TWO)?.fileUrl}
              onViewClick={(filePath, fileName) => {
                setDocumentViewerUrl(filePath);
                setDocumentViewerName(fileName);
                setShowDocumentViewer(true);
              }}
            />
          </div>
        </div>
      </div>
      <div className="row m-2">
        <div className="col-lg-6 col-md-6 col-sm-12">
          <div className="input-blocks">
            <label>Mold Specifications Document</label>
            <SimpleFileUpload
              id={MOLD_RECEIVING.MOLD_SPECIFICATIONS_DOCUMENT}
              onChange={handleFileUpload}
              value={getDocumentPath(MOLD_RECEIVING.MOLD_SPECIFICATIONS_DOCUMENT)?.fileName}
              filePath={getDocumentPath(MOLD_RECEIVING.MOLD_SPECIFICATIONS_DOCUMENT)?.fileUrl}
              onViewClick={(filePath, fileName) => {
                setDocumentViewerUrl(filePath);
                setDocumentViewerName(fileName);
                setShowDocumentViewer(true);
              }}
            />
          </div>
        </div>
        <div className="col-lg-6 col-md-6 col-sm-12">
          <div className="input-blocks">
            <label>Previous Maintenance Records(if any)</label>
            <SimpleFileUpload
              id={MOLD_RECEIVING.PREVIOUS_MAINTENANCE_RECORDS}
              onChange={handleFileUpload}
              value={getDocumentPath(MOLD_RECEIVING.PREVIOUS_MAINTENANCE_RECORDS)?.fileName}
              filePath={getDocumentPath(MOLD_RECEIVING.PREVIOUS_MAINTENANCE_RECORDS)?.fileUrl}
              onViewClick={(filePath, fileName) => {
                setDocumentViewerUrl(filePath);
                setDocumentViewerName(fileName);
                setShowDocumentViewer(true);
              }}
            />
          </div>
        </div>
      </div>
      {/* Picture Uploads*/}
      <h5 className="header-five">Picture Uploads</h5>
      <div className="row m-2">
        <div className="col-lg-6 col-md-6 col-sm-12">
          <div className="input-blocks">
            <label>Mold Picture on the Trailer</label>
            <WebcamFileUpload
              id={MOLD_RECEIVING.MOLD_PICTURE_ON_THE_TRAILER}
              onChange={updateMoldDocuments}
              documents={convertDocumentsForWebCamFileUpload(
                MOLD_RECEIVING.MOLD_PICTURE_ON_THE_TRAILER,
              )}
              context="receiving"
              maintenanceData={formData}
              buttonLabel="Mold Picture on the Trailer"
              showManageImages={hasImagesForField(MOLD_RECEIVING.MOLD_PICTURE_ON_THE_TRAILER)}
              moldInfoID={formData.moldInfoID}
              onMaintenanceSaved={handleMoldInfoIDSaved}
            />
          </div>
          <div className="input-blocks">
            <label>Mold Picture Unloaded</label>
            <WebcamFileUpload
              id={MOLD_RECEIVING.MOLD_PICTURE_UNLOADED}
              onChange={updateMoldDocuments}
              documents={convertDocumentsForWebCamFileUpload(MOLD_RECEIVING.MOLD_PICTURE_UNLOADED)}
              context="receiving"
              maintenanceData={formData}
              buttonLabel="Mold Picture Unloaded"
              showManageImages={hasImagesForField(MOLD_RECEIVING.MOLD_PICTURE_UNLOADED)}
              moldInfoID={formData.moldInfoID}
              onMaintenanceSaved={handleMoldInfoIDSaved}
            />
          </div>
          <div className="input-blocks">
            <label>Mold Picture open</label>
            <WebcamFileUpload
              id={MOLD_RECEIVING.MOLD_PICTURE_OPEN}
              onChange={updateMoldDocuments}
              documents={convertDocumentsForWebCamFileUpload(MOLD_RECEIVING.MOLD_PICTURE_OPEN)}
              context="receiving"
              maintenanceData={formData}
              buttonLabel="Mold Picture open"
              showManageImages={hasImagesForField(MOLD_RECEIVING.MOLD_PICTURE_OPEN)}
              moldInfoID={formData.moldInfoID}
              onMaintenanceSaved={handleMoldInfoIDSaved}
            />
          </div>
          <div className="input-blocks">
            <label>Additional Picture 1 </label>
            <WebcamFileUpload
              id={MOLD_RECEIVING.ADDITIONAL_PICTURES_ONE}
              onChange={updateMoldDocuments}
              documents={convertDocumentsForWebCamFileUpload(
                MOLD_RECEIVING.ADDITIONAL_PICTURES_ONE,
              )}
              context="receiving"
              maintenanceData={formData}
              buttonLabel="Additional Picture 1"
              showManageImages={hasImagesForField(MOLD_RECEIVING.ADDITIONAL_PICTURES_ONE)}
              moldInfoID={formData.moldInfoID}
              onMaintenanceSaved={handleMoldInfoIDSaved}
            />
          </div>
        </div>

        <div className="col-lg-6 col-md-6 col-sm-12">
          <div className="input-blocks">
            <label>Additional Picture 2 </label>
            <WebcamFileUpload
              id={MOLD_RECEIVING.ADDITIONAL_PICTURES_TWO}
              onChange={updateMoldDocuments}
              documents={convertDocumentsForWebCamFileUpload(
                MOLD_RECEIVING.ADDITIONAL_PICTURES_TWO,
              )}
              context="receiving"
              maintenanceData={formData}
              buttonLabel="Additional Picture 2"
              showManageImages={hasImagesForField(MOLD_RECEIVING.ADDITIONAL_PICTURES_TWO)}
              moldInfoID={formData.moldInfoID}
              onMaintenanceSaved={handleMoldInfoIDSaved}
            />
            <label>Additional Picture 3</label>
            <WebcamFileUpload
              id={MOLD_RECEIVING.ADDITIONAL_PICTURES_THREE}
              onChange={updateMoldDocuments}
              documents={convertDocumentsForWebCamFileUpload(
                MOLD_RECEIVING.ADDITIONAL_PICTURES_THREE,
              )}
              context="receiving"
              maintenanceData={formData}
              buttonLabel="Additional Picture 3"
              showManageImages={hasImagesForField(MOLD_RECEIVING.ADDITIONAL_PICTURES_THREE)}
              moldInfoID={formData.moldInfoID}
              onMaintenanceSaved={handleMoldInfoIDSaved}
            />
            <label>Additional Picture 4 </label>
            <WebcamFileUpload
              id={MOLD_RECEIVING.ADDITIONAL_PICTURES_FOUR}
              onChange={updateMoldDocuments}
              documents={convertDocumentsForWebCamFileUpload(
                MOLD_RECEIVING.ADDITIONAL_PICTURES_FOUR,
              )}
              context="receiving"
              maintenanceData={formData}
              buttonLabel="Additional Picture 4"
              showManageImages={hasImagesForField(MOLD_RECEIVING.ADDITIONAL_PICTURES_FOUR)}
              moldInfoID={formData.moldInfoID}
              onMaintenanceSaved={handleMoldInfoIDSaved}
            />
            <label>Additional Picture 5</label>
            <WebcamFileUpload
              id={MOLD_RECEIVING.ADDITIONAL_PICTURES_FIVE}
              onChange={updateMoldDocuments}
              documents={convertDocumentsForWebCamFileUpload(
                MOLD_RECEIVING.ADDITIONAL_PICTURES_FIVE,
              )}
              context="receiving"
              maintenanceData={formData}
              buttonLabel="Additional Picture 5"
              showManageImages={hasImagesForField(MOLD_RECEIVING.ADDITIONAL_PICTURES_FIVE)}
              moldInfoID={formData.moldInfoID}
              onMaintenanceSaved={handleMoldInfoIDSaved}
            />
            <label>Additional Picture 6 </label>
            <WebcamFileUpload
              id={MOLD_RECEIVING.ADDITIONAL_PICTURES_SIX}
              onChange={updateMoldDocuments}
              documents={convertDocumentsForWebCamFileUpload(
                MOLD_RECEIVING.ADDITIONAL_PICTURES_SIX,
              )}
              context="receiving"
              maintenanceData={formData}
              buttonLabel="Additional Picture 6"
              showManageImages={hasImagesForField(MOLD_RECEIVING.ADDITIONAL_PICTURES_SIX)}
              moldInfoID={formData.moldInfoID}
              onMaintenanceSaved={handleMoldInfoIDSaved}
            />
          </div>
        </div>
      </div>
      {/* Inspection Checklist*/}
      <h5 className="header-five">Inspection Checklist</h5>
      <div className="row m-2">
        <div className="col-lg-6 col-md-6 col-sm-12">
          <div className="input-blocks">
            <label>Visual Inspection :</label>

            <div className="row ms-5">
              <div className="col-lg-3 col-md-3 col-sm-12">
                <div className="input-blocks">
                  <label>Visible damage :</label>
                </div>
              </div>

              <div className="col-lg-3 col-md-3 col-sm-12">
                <div
                  className="btn-group"
                  role="group"
                  aria-label="Basic radio toggle button group"
                >
                  <input
                    type="radio"
                    className="btn-check"
                    name="btnradioNoVisDam"
                    id="btnradioNoVisDamYes"
                    // defaultChecked=""
                    checked={formData.visualInspectionDamage == '1'}
                    value="1"
                    onChange={e => {
                      updateFormField('visualInspectionDamage', e.target.value);
                    }}
                  />
                  <label className="btn btn-outline-primary" htmlFor="btnradioNoVisDamYes">
                    Yes
                  </label>
                  <input
                    type="radio"
                    className="btn-check"
                    name="btnradioNoVisDam"
                    id="btnradioNoVisDamNo"
                    checked={formData.visualInspectionDamage == '0'}
                    value="0"
                    onChange={e => {
                      updateFormField('visualInspectionDamage', e.target.value);
                    }}
                  />
                  <label className="btn btn-outline-primary" htmlFor="btnradioNoVisDamNo">
                    No
                  </label>
                </div>
              </div>
            </div>

            <div className="row ms-5">
              <div className="col-lg-3 col-md-3 col-sm-12">
                <div className="input-blocks">
                  <label>Rust or corrosion :</label>
                </div>
              </div>

              <div className="col-lg-3 col-md-3 col-sm-12">
                <div
                  className="btn-group"
                  role="group"
                  aria-label="Basic radio toggle button group"
                >
                  <input
                    type="radio"
                    className="btn-check"
                    name="btnradioNoRust"
                    id="btnradioNoRustYes"
                    defaultChecked=""
                    checked={formData.visualInspectionRust == '1'}
                    value="1"
                    onChange={e => {
                      updateFormField('visualInspectionRust', e.target.value);
                    }}
                  />
                  <label className="btn btn-outline-primary" htmlFor="btnradioNoRustYes">
                    Yes
                  </label>
                  <input
                    type="radio"
                    className="btn-check"
                    name="btnradioNoRust"
                    id="btnradioNoRustNo"
                    checked={formData.visualInspectionRust == '0'}
                    value="0"
                    onChange={e => {
                      updateFormField('visualInspectionRust', e.target.value);
                    }}
                  />
                  <label className="btn btn-outline-primary" htmlFor="btnradioNoRustNo">
                    No
                  </label>
                </div>
              </div>
            </div>

            <div className="row ms-5">
              <div className="col-lg-3 col-md-3 col-sm-12">
                <div className="input-blocks">
                  <label>Proper labelling :</label>
                </div>
              </div>

              <div className="col-lg-3 col-md-3 col-sm-12">
                <div
                  className="btn-group"
                  role="group"
                  aria-label="Basic radio toggle button group"
                >
                  <input
                    type="radio"
                    className="btn-check"
                    name="btnradioPropLab"
                    id="btnradioPropLabYes"
                    defaultChecked=""
                    checked={formData.visualInspectionLabeling == '1'}
                    value="1"
                    onChange={e => {
                      updateFormField('visualInspectionLabeling', e.target.value);
                    }}
                  />
                  <label className="btn btn-outline-primary" htmlFor="btnradioPropLabYes">
                    Yes
                  </label>
                  <input
                    type="radio"
                    className="btn-check"
                    name="btnradioPropLab"
                    id="btnradioPropLabNo"
                    checked={formData.visualInspectionLabeling == '0'}
                    value="0"
                    onChange={e => {
                      updateFormField('visualInspectionLabeling', e.target.value);
                    }}
                  />
                  <label className="btn btn-outline-primary" htmlFor="btnradioPropLabNo">
                    No
                  </label>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="col-lg-6 col-md-6 col-sm-12">
          <div className="input-blocks">
            <label>Dimensional Inspection :</label>
            <div className="d-flex">
              <div className="input-blocks">
                <label>Mold Width : </label>
                <input
                  type="number"
                  step="0.01"
                  value={formData.moldWidth / 100}
                  onChange={e => updateFormField('moldWidth', e.target.value)}
                  className="form-control"
                  placeholder="Width"
                />
              </div>

              <span className="text-xl px-4 pt-2">X</span>
              <div className="input-blocks">
                <label>Mold Height : </label>
                <input
                  type="number"
                  step="0.01"
                  value={formData.moldHeight / 100}
                  onChange={e => updateFormField('moldHeight', e.target.value)}
                  className="form-control"
                  placeholder="Height"
                />
              </div>
              <span className="text-xl px-4 pt-2">X</span>
              <div className="input-blocks">
                <label>Mold Length : </label>
                <input
                  type="number"
                  step="0.01"
                  value={formData.moldLength / 100}
                  onChange={e => updateFormField('moldLength', e.target.value)}
                  className="form-control"
                  placeholder="Length"
                />
              </div>
            </div>
          </div>
          <div className="input-blocks">
            <label>Functional Test</label>
            <div className="d-flex">
              <div
                className="btn-group me-3"
                role="group"
                aria-label="Basic radio toggle button group"
              >
                <input
                  type="radio"
                  className="btn-check"
                  name="btnradioFnTest"
                  id="btnradioFnTestPass"
                  defaultChecked=""
                  checked={formData.functionalTestResult == 'Pass'}
                  value="Pass"
                  onChange={e => {
                    updateFormField('functionalTestResult', e.target.value);
                  }}
                />
                <label className="btn btn-outline-primary" htmlFor="btnradioFnTestPass">
                  Pass
                </label>
                <input
                  type="radio"
                  className="btn-check"
                  name="btnradioFnTest"
                  id="btnradioFnTestFail"
                  checked={formData.functionalTestResult == 'Fail'}
                  value="Fail"
                  onChange={e => {
                    updateFormField('functionalTestResult', e.target.value);
                  }}
                />
                <label className="btn btn-outline-primary" htmlFor="btnradioFnTestFail">
                  Fail
                </label>
              </div>
              <SimpleFileUpload
                id={MOLD_RECEIVING.FUNCTIONAL_TEST}
                onChange={handleFileUpload}
                value={getDocumentPath(MOLD_RECEIVING.FUNCTIONAL_TEST)?.fileName}
                filePath={getDocumentPath(MOLD_RECEIVING.FUNCTIONAL_TEST)?.fileUrl}
                className="h-100"
              />
            </div>
          </div>
        </div>
      </div>

      <div className="row m-2">
        <div className="col-lg-6 col-md-6 col-sm-12"></div>

        <div className="col-lg-6 col-md-6 col-sm-12"></div>
      </div>
      {/* Comments/Notes*/}
      <h5 className="header-five">Comments/Notes</h5>
      <div className="row m-2">
        <div className="col-lg-12 col-md-12 col-sm-12">
          <div className="input-blocks">
            <label>Additional Comments</label>
            {/* <TextEditor
                  data={formData.additionalComments}
                  onChange={e => updateFormField('additionalComments', e.target.data)}
                /> */}
            <TextEditor
              data={formData.additionalComments || ''}
              onChange={event => {
                updateFormField('additionalComments', event);
              }}
            />
          </div>
        </div>
      </div>
      {/* Approval*/}
      <h5 className="header-five">Approval</h5>
      <div className="row m-2">
        <div className="col-lg-6 col-md-6 col-sm-12">
          <div className="input-blocks">
            <label>Unloading Approval</label>
            <input
              type="text"
              value={formData.unloadingApproval}
              onChange={e => updateFormField('unloadingApproval', e.target.value)}
              className="form-control"
              placeholder="Name"
            />
          </div>
        </div>
        <div className="col-lg-6 col-md-6 col-sm-12">
          <div className="input-blocks">
            <label>Quality Control Approved By</label>
            <input
              type="text"
              value={formData.qualityControlApprovedBy}
              onChange={e => updateFormField('qualityControlApprovedBy', e.target.value)}
              className="form-control"
              placeholder="Name"
            />
          </div>
        </div>
      </div>
      <div className="row m-2">
        <div className="col-lg-6 col-md-6 col-sm-12">
          <div className="input-blocks">
            <label>Engineering Approved By</label>
            <input
              type="text"
              value={formData.engineeringApprovedBy}
              onChange={e => updateFormField('engineeringApprovedBy', e.target.value)}
              className="form-control"
              placeholder="Name"
            />
          </div>
        </div>
        <div className="col-lg-6 col-md-6 col-sm-12">
          <div className="input-blocks">
            <label>Submitted By</label>
            <input
              type="text"
              value={formData.submittedBy}
              onChange={e => updateFormField('submittedBy', e.target.value)}
              className="form-control"
              placeholder="Name"
            />
          </div>
        </div>
      </div>
    </form>

    {/* Document Viewer Modal */}
    <DocumentViewerModal
      show={showDocumentViewer}
      onHide={() => setShowDocumentViewer(false)}
      documentUrl={documentViewerUrl}
      documentName={documentViewerName}
    />
    </>
  );
};
MoldReceivingAddEditForm.propTypes = {
  invalidFields: PropTypes.array,
  showValidation: PropTypes.bool,
};
export default MoldReceivingAddEditForm;
